import * as React from 'react';

import { IBcPoNumberData } from './bc-po-number.data';
import { IBcPoNumberProps } from './bc-po-number.props.autogenerated';

import { IModuleStateProps, withModuleState } from '@msdyn365-commerce-modules/checkout-utilities';
import { observer } from 'mobx-react';
import { getCheckoutState } from '@msdyn365-commerce/global-state';
import { updateAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CartsDataActions.g';
import { AttributeValue } from '@msdyn365-commerce/retail-proxy';

export interface IBcPoNumberWithCommentboxViewProps extends IBcPoNumberProps<IBcPoNumberData> {
    input: React.ReactNode;
    poNumber: React.ReactNode;
}

interface IBcCheckoutPoNumberModuleProps extends IBcPoNumberProps<IBcPoNumberData>, IModuleStateProps {}

interface IBcCheckoutPONumberState {
    poNumber: string;
    originalPoNumber: string;
    showError: boolean;
}

/**
 *
 * BcPoNumberWithCommentbox component
 * @extends {React.PureComponent<IBcPoNumberProps<IBcPoNumberData>>}
 */
// @ts-expect-error
@withModuleState
@observer
class BcPoNumberWithCommentbox extends React.PureComponent<IBcCheckoutPoNumberModuleProps, IBcCheckoutPONumberState> {
    public state: IBcCheckoutPONumberState = {
        poNumber: '',
        originalPoNumber: '',
        showError: false
    };

    public componentDidMount(): void {
        this.init();
    }

    public handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        this.setState({ poNumber: value, showError: value === '' });
    };

    public render(): JSX.Element | null {
        const { moduleState } = this.props;
        const viewProps = {
            ...this.props,
            input: moduleState.isUpdating && (
                <div className={`${this.state.poNumber === '' || this.state.poNumber.includes(' ') ? 'address-form__item-invalid' : ''}`}>
                    <input
                        className='ms-checkout-guest-profile__input-text form-control'
                        type='text'
                        placeholder={this.props.config.inputPlaceholder}
                        value={this.state.poNumber}
                        onChange={this.handleInputChange}
                        maxLength={this.props.config.maxCharacterLength}
                    />
                    {/* showing the spaces Error here */}
                    {this.state.poNumber.includes(' ') && (
                        <span
                            className='msc-address-form__alert'
                            role='alert'
                            aria-live='assertive'
                            style={{ display: this.state.poNumber.includes(' ') ? 'inline' : 'none' }}
                        >
                            Spaces are not allowed
                        </span>
                    )}
                    {this.state.showError && (
                        <span className='msc-address-form__alert' role='alert' aria-live='assertive' style={{ display: 'inline' }}>
                            PO Number is required
                        </span>
                    )}
                </div>
            ),
            poNumber: (moduleState.isReady || moduleState.isPending) && (
                <span className='ms-checkout-guest-profile__selected-email'>{this.state.originalPoNumber}</span>
            )
        };

        return this.props.renderView(viewProps);
    }

    private readonly init = (): void => {
        const { poNumberAttributeName } = this.props.context.app.config;
        const checkoutState = this.props.data.checkout.result;
        const cart = checkoutState?.checkoutCart.cart;
        const checkoutAttributes = cart?.AttributeValues as AttributeValue[];
        const poNumberAttribute = checkoutAttributes?.find(attr => attr.Name === poNumberAttributeName);
        if (
            poNumberAttribute &&
            poNumberAttribute.TextValue &&
            poNumberAttribute.TextValue !== '' &&
            poNumberAttribute.TextValue !== 'None'
        ) {
            this.setState({ poNumber: poNumberAttribute.TextValue, originalPoNumber: poNumberAttribute.TextValue });
        }
        this.props.moduleState.init({
            onEdit: this.onEdit,
            onCancel: this.onCancel,
            onSubmit: this.onSubmit,
            status: poNumberAttribute && poNumberAttribute.TextValue !== '' && poNumberAttribute.TextValue !== 'None' ? 'ready' : 'updating'
        });
        if (checkoutState) {
            if (checkoutState.checkoutCart.hasInvoiceLine) {
                this.props.moduleState.onSkip();
                this.props.moduleState.onDisable();
            }
        }
    };

    private readonly onCancel = (): void => {
        this.props.telemetry.information('PO Number onCancel is called.');
        this.setState(
            prevState => ({
                poNumber: prevState.originalPoNumber,
                showError: false
            }),
            () => {
                if (this.state.poNumber !== '' || this.state.poNumber.includes(' ')) {
                    this.props.moduleState.onReady();
                } else {
                    this.props.moduleState.onUpdating();
                }
            }
        );
    };

    private readonly onEdit = (): void => {
        this.props.telemetry.information('PO Number onEdit is called.');
        this.props.moduleState.onUpdating();
    };

    private readonly onSubmit = async (): Promise<void> => {
        const { poNumber } = this.state;
        if (poNumber === '') {
            this.setState({ showError: true });
            return;
        }
        if (poNumber.includes(' ')) {
            return;
        }
        this.onPending();
        const { poNumberAttributeName } = this.props.context.app.config;
        const checkoutState = await getCheckoutState(this.props.context.actionContext);
        const cart = checkoutState.checkoutCart.cart;
        const checkoutAttributes = cart.AttributeValues as AttributeValue[];
        const poNumberAttributeIndex = checkoutAttributes?.findIndex(attr => attr.Name === poNumberAttributeName);
        if (poNumberAttributeIndex >= 0) {
            checkoutAttributes[poNumberAttributeIndex].TextValue = poNumber;
        } else {
            const attributeObj = {
                // @ts-ignore
                '@odata.type': '#Microsoft.Dynamics.Commerce.Runtime.DataModel.AttributeTextValue',
                Name: poNumberAttributeName,
                TextValue: poNumber,
                TextValueTranslations: []
            };
            checkoutAttributes.push(attributeObj);
        }
        await checkoutState.checkoutCart.updateAttributeValues({
            newAttributeValues: checkoutAttributes
        });
        const updatedCheckoutState = await getCheckoutState(this.props.context.actionContext);

        await updateAsync({ callerContext: this.props.context.actionContext }, updatedCheckoutState.checkoutCart.cart);
        await checkoutState.checkoutCart.refreshCart({});
        this.setState({ originalPoNumber: poNumber, showError: false });
        this.props.telemetry.information('PO Number onSubmit is called.');

        this.props.moduleState.onReady();
    };

    private readonly onPending = () => {
        this.props.moduleState.onPending();
        if (this.props.data && this.props.data.checkout && this.props.data.checkout.result) {
            if (this.props.data.checkout.result.checkoutCart.hasInvoiceLine) {
                this.props.moduleState.onSkip();
                this.props.moduleState.onDisable();
            }
        }
    };
}

export default BcPoNumberWithCommentbox;
